import React, { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import HighStock from 'highcharts/highstock';
import type { Options as HighchartsOptions } from 'highcharts';
import HCAccessibility from 'highcharts/modules/accessibility';
import HighchartsReact from 'highcharts-react-official';
import HCExporting from 'highcharts/modules/exporting';
import HCOfflineExporting from 'highcharts/modules/offline-exporting';
import HCNoData from 'highcharts/modules/no-data-to-display';
import moment from 'moment';
import reactGA from 'react-ga';
import { bindLineChartTooltipVisibility, lineChartHighchartsTooltipCfg } from '~/shared/utils/lineChartTooltip';
import { getLineChartExportMenuItems, registerLineChartExport } from '~/shared/utils/lineChartExport';
import { buildChartExportFileName } from '~/shared/utils/buildChartExportFileName';
import { hideLineChartLegendTooltip, syncLineChartLegendTooltips } from '~/shared/utils/lineChartLegendTooltip';
import {
  syncLineChartSelectedSeriesFromChart,
  useLineChartLegendClickHandlers,
} from '~/shared/utils/lineChartSelectionSync';
import { registerHighchartsErrorLogger } from '~/shared/utils/registerHighchartsErrorLogger';
import {
  deriveNonNegativeAxisFloor,
  deriveAxisLimits,
  normalizePoint,
  readFiniteXExtentsFromAxis,
  xExtentsDiffer,
  pickXExtentsFromAfterSetExtremesEvent,
  shouldPersistUserXExtentsFromEvent,
  type PointLike,
} from '~/shared/utils/chartAxis';
import {
  applyRangeSelectorToChart,
  areChartXExtremesCanonicalForRangeIndex,
  DEFAULT_RANGE_SELECTOR_INDEX,
  extractSeriesXValues,
  MAX_RANGE_SELECTOR_INDEX,
  readMainAxisMaxForDurationPreset,
} from '~/shared/utils/timeWindow';
import { Root, HighchartContainer } from './YAxisLineChart.styles';

HCExporting(HighStock);
HCNoData(HighStock);
HCOfflineExporting(HighStock);
HCAccessibility(HighStock);

const ensureHighchartsLocale = (() => {
  let applied = false;
  const fallbackWeekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const fallbackShortWeekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const fallbackMonths = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const fallbackShortMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const isValid = (values: unknown[] | undefined, expected: number) =>
    Array.isArray(values) && values.length === expected && values.every(value => typeof value === 'string');

  return () => {
    if (applied) return;
    const langOptions = HighStock.getOptions().lang || {};
    const needsWeekdays = !isValid(langOptions.weekdays, 7);
    const needsShortWeekdays = !isValid(langOptions.shortWeekdays, 7);
    const needsMonths = !isValid(langOptions.months, 12);
    const needsShortMonths = !isValid(langOptions.shortMonths, 12);

    if (needsWeekdays || needsShortWeekdays || needsMonths || needsShortMonths) {
      HighStock.setOptions({
        lang: {
          weekdays: needsWeekdays ? fallbackWeekdays : langOptions.weekdays,
          shortWeekdays: needsShortWeekdays ? fallbackShortWeekdays : langOptions.shortWeekdays,
          months: needsMonths ? fallbackMonths : langOptions.months,
          shortMonths: needsShortMonths ? fallbackShortMonths : langOptions.shortMonths,
        },
      });
    }

    applied = true;
  };
})();

ensureHighchartsLocale();

registerHighchartsErrorLogger(HighStock);
registerLineChartExport(HighStock);

let exportMenuIdCounter = 0;

const EMPTY_STOCK_OPTIONS: HighchartsOptions = { series: [] };
const DEFAULT_GRID_HEIGHT = 4;
const MIN_GRID_HEIGHT = 1;
const getFilterFormattedDate = (inputDate: number) =>
  moment(Number(String(inputDate).substring(0, 9) + '0000')).format('YYYY-MM-DD');

HighStock.SVGRenderer.prototype.symbols.ellipsisvertical = function () {
  const path = [
    'M18.1538 16C18.1538 17.1451 17.222 18.0769 16.0769 18.0769C14.9318 18.0769 14 17.1451 14 16C14 14.8549 14.9318 13.9231 16.0769 13.9231C17.222 13.9231 18.1538 14.8549 18.1538 16Z M18.1538 22.9231C18.1538 24.0682 17.222 25 16.0769 25C14.9318 25 14 24.0682 14 22.9231C14 21.778 14.9318 20.8462 16.0769 20.8462C17.222 20.8462 18.1538 21.778 18.1538 22.9231Z M18.1538 9.07692C18.1538 10.222 17.222 11.1538 16.0769 11.1538C14.9318 11.1538 14 10.222 14 9.07692C14 7.93185 14.9318 7 16.0769 7C17.222 7 18.1538 7.93185 18.1538 9.07692Z',
  ];
  return path;
};

export default function YAxisLineChart({
  data0,
  data1,
  size,
  onClick,
  prism,
  filterValues,
  chartTitle = ' ',
  chartSubtitle,
  chartYtitle,
  tooltipCfg,
  gridHeight,
  gridIndex,
  data,
  plotBands,
  numberOfLines,
  smaValue,
  selectedRangeSelector,
  setSelectedRangeSelector,
  showMean = false,
  onShowMeanChange,
  onMeanToggleStatusChange,
  onChartReady,
  onTooltipVisibilityChange,
  selectedSeriesNames: selectedSeriesNamesProp,
  onSelectedSeriesNamesChange,
  onSeriesMetadataChange,
  smaEnabled,
  meanEnabled,
  dropdownLegendEnabled,
  onExportMenuVisibilityChange,
  onTopicExplainabilityRequest,
  topicForExplainability,
  yAxisRangeMode = 'padded-visible',
  yAxisPaddingRatio = 0.2,
  yAxisMin,
  yAxisMax,
}: any) {
  const chartRef = useRef<any>(null);
  const readySignaledRef = useRef(false);
  const savedUserXExtentsRef = useRef<{ min: number; max: number } | null>(null);
  const [xRangeUserCustom, setXRangeUserCustom] = useState(false);
  const xRangeUserCustomRef = useRef(false);
  const applyingRangePresetRef = useRef(false);
  const pendingDurationPresetAnchorRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    xRangeUserCustomRef.current = xRangeUserCustom;
  }, [xRangeUserCustom]);
  const exportMenuClassName = useMemo(() => {
    exportMenuIdCounter += 1;
    return `highcharts-indicator-menu-${exportMenuIdCounter}`;
  }, []);
  const combinedMenuClassName = `highcharts-indicator-menu ${exportMenuClassName}`;

  const queueReadySignal = useCallback(() => {
    if (!onChartReady || readySignaledRef.current) return;
    const chart = chartRef?.current?.chart;
    if (!chart) return;

    const handleRender = function handleRender(this: HighStock.Chart) {
      if (readySignaledRef.current) return;

      const hasData = this.series
        ?.filter(series => series && series.name && !/^Navigator \d+$/.test(series.name))
        ?.some(series => {
          const points = Array.isArray(series.points) ? series.points : [];
          if (points.length) {
            return true;
          }
          const seriesOptions = series.options as any;
          const data = Array.isArray(seriesOptions?.data) ? seriesOptions.data : [];
          return data.length > 0;
        });

      if (!hasData) {
        return;
      }

      readySignaledRef.current = true;
      onChartReady();
      HighStock.removeEvent(this, 'render', handleRender as any);
    };

    HighStock.addEvent(chart, 'render', handleRender as any);
  }, [onChartReady]);

  const [internalSelectedSeriesNames, setInternalSelectedSeriesNames] = useState<string[] | null>(null);
  const selectedSeriesNames = selectedSeriesNamesProp ?? internalSelectedSeriesNames ?? null;
  const updateSelectedSeriesNames = useCallback(
    (next: string[] | null) => {
      if (onSelectedSeriesNamesChange) {
        onSelectedSeriesNamesChange(next);
      } else {
        setInternalSelectedSeriesNames(next);
      }
    },
    [onSelectedSeriesNamesChange],
  );

  const latestUpdateSelectedSeriesNamesRef = useRef(updateSelectedSeriesNames);
  useEffect(() => {
    latestUpdateSelectedSeriesNamesRef.current = updateSelectedSeriesNames;
  }, [updateSelectedSeriesNames]);

  useEffect(() => {
    return () => {
      latestUpdateSelectedSeriesNamesRef.current(null);
    };
  }, []);

  const [doneHide, setDoneHide] = useState(false);
  const containerWidth = size?.width;
  const isNarrowChart = containerWidth && containerWidth < 600;

  const baseSeries = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.filter((series: any) => series && series.name && !/^Navigator \d+$/.test(series.name));
  }, [data]);

  const baseSeriesNames = useMemo(() => baseSeries.map((series: any) => series.name), [baseSeries]);

  const normalizeSelection = useCallback(
    (selection: string[] | null | undefined) => {
      if (selection === null || selection === undefined) {
        return baseSeriesNames;
      }
      const selectionSet = new Set(selection);
      return baseSeriesNames.filter(name => selectionSet.has(name));
    },
    [baseSeriesNames],
  );

  const areSelectionsEqual = useCallback(
    (a: string[] | null | undefined, b: string[] | null | undefined) => {
      if (a === b) return true;
      const normalizedA = normalizeSelection(a);
      const normalizedB = normalizeSelection(b);
      if (normalizedA.length !== normalizedB.length) {
        return false;
      }
      return normalizedA.every((value, index) => normalizedB[index] === value);
    },
    [normalizeSelection],
  );

  const resolvedSelectedNames = useMemo(
    () => normalizeSelection(selectedSeriesNames),
    [normalizeSelection, selectedSeriesNames],
  );

  const baseSeriesCount = baseSeries.length;
  const activeSeriesCount = resolvedSelectedNames.length;
  const canShowMeanToggle = baseSeriesCount > 1;
  const meanToggleDisabled = activeSeriesCount < 2;

  const resolvedGridHeight = useMemo(() => {
    const numericHeight = typeof gridHeight === 'number' && Number.isFinite(gridHeight) ? gridHeight : null;

    if (numericHeight === null || numericHeight <= 0) {
      return DEFAULT_GRID_HEIGHT;
    }

    if (numericHeight < MIN_GRID_HEIGHT) {
      return MIN_GRID_HEIGHT;
    }

    return numericHeight;
  }, [gridHeight]);

  const chartHeight = useMemo(() => {
    const gridUnits = resolvedGridHeight / 4;
    const computed = gridUnits * 446 + (gridUnits - 1) * 38;
    if (Number.isFinite(computed) && computed > 0) {
      return computed;
    }
    return 446;
  }, [resolvedGridHeight]);

  useEffect(() => {
    setDoneHide(false);
  }, [data, numberOfLines]);

  const normalizedTopicForExplainability = useMemo(() => {
    if (!topicForExplainability) return '';
    return String(topicForExplainability).trim();
  }, [topicForExplainability]);

  useEffect(() => {
    if (!baseSeries.length) {
      if (selectedSeriesNames !== null) {
        updateSelectedSeriesNames(null);
      }
      return;
    }

    if (selectedSeriesNames === null) {
      return;
    }

    if (!Array.isArray(selectedSeriesNames)) {
      updateSelectedSeriesNames(null);
      return;
    }

    if (selectedSeriesNames.length === 0) {
      // Intentionally allow empty selections so the chart can render with no visible series.
      return;
    }

    const baseNameSet = new Set(baseSeriesNames);
    const filtered = selectedSeriesNames.filter(name => baseNameSet.has(name));

    if (!filtered.length) {
      updateSelectedSeriesNames(null);
      return;
    }

    if (filtered.length === selectedSeriesNames.length) {
      return;
    }

    updateSelectedSeriesNames(filtered);
  }, [baseSeries, baseSeriesNames, selectedSeriesNames, updateSelectedSeriesNames]);

  useEffect(() => {
    if (!baseSeries.length) return;
    if (selectedSeriesNames === null) return;
    if (!Array.isArray(selectedSeriesNames)) return;

    if (
      selectedSeriesNames.length === baseSeriesNames.length &&
      selectedSeriesNames.every(name => baseSeriesNames.includes(name))
    ) {
      updateSelectedSeriesNames(null);
    }
  }, [baseSeries.length, baseSeriesNames, selectedSeriesNames, updateSelectedSeriesNames]);

  useEffect(() => {
    if (!onSeriesMetadataChange) return;
    const seriesMeta = baseSeries.map((series: any) => {
      const colorCandidate =
        series?.color ?? series?.lineColor ?? series?.marker?.lineColor ?? series?.marker?.fillColor;
      const isVisible =
        selectedSeriesNames === null ||
        (Array.isArray(selectedSeriesNames) && selectedSeriesNames.includes(series.name));
      const description = series?.custom?.description ?? series?.description;
      return {
        name: series.name,
        color: colorCandidate,
        visible: isVisible,
        ...(typeof description === 'string' && description.trim() ? { description } : {}),
      };
    });
    onSeriesMetadataChange(seriesMeta);
  }, [baseSeries, onSeriesMetadataChange, selectedSeriesNames]);

  useEffect(() => {
    if (!onShowMeanChange) return;
    if (baseSeries.length < 2 || (showMean && meanToggleDisabled)) {
      if (showMean) {
        onShowMeanChange(false);
      }
    }
  }, [baseSeries, showMean, meanToggleDisabled, onShowMeanChange]);

  const exportFileName = buildChartExportFileName(chartTitle, filterValues, prism);

  useEffect(() => {
    const onScroll = () => {
      const elements = document.getElementsByClassName('xLabel');

      for (let i = 0; i < elements.length; i++) {
        (elements[i] as unknown as any).style.display = 'none';
      }

      const chart = chartRef?.current?.chart;
      if (chart) {
        hideLineChartLegendTooltip(chart);
      }
    };
    // clean up code
    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    function handleClick(e: any) {
      const file = (e.target as Element).getAttribute('download');
      if (file) {
        const fileType = file.substr(file.length - 3, 3);
        reactGA.event({ category: 'User', action: `Downloaded ${fileType}: ${file}` });
      }
    }
    window.addEventListener('click', handleClick);

    return function cleanupListener() {
      window.removeEventListener('click', handleClick);
    };
  }, [chartRef]);

  const updateSelectedSeriesFromChart = useCallback(
    (chartInstance?: HighStock.Chart, revealedSeriesName?: string, allowCollapseToEmpty = false) => {
      const chart = chartInstance ?? chartRef?.current?.chart;
      if (!chart) return;

      syncLineChartSelectedSeriesFromChart({
        chart,
        baseSeriesNames,
        selectedSeriesNames,
        areSelectionsEqual,
        setSelectedSeriesNames: updateSelectedSeriesNames,
        revealedSeriesName,
        allowCollapseToEmpty,
      });
    },
    [areSelectionsEqual, baseSeriesNames, chartRef, selectedSeriesNames, updateSelectedSeriesNames],
  );

  const { legendEventsItemClick } = useLineChartLegendClickHandlers({
    baseSeriesNames,
    selectedSeriesNames,
    setSelectedSeriesNames: updateSelectedSeriesNames,
  });

  useEffect(() => {
    const chart = chartRef?.current?.chart;
    if (!chart) return;
    updateSelectedSeriesFromChart(chart);
    // Only re-sync when the underlying series set changes, not on every selection update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseSeries]);

  useEffect(() => {
    const chart = chartRef?.current?.chart;
    if (!chart) return;

    chart.reflow();

    if (!chart.navigator) {
      chart.update(
        {
          navigator: {
            enabled: true,
            series: { fillOpacity: 0 },
          },
        },
        false,
      );
    } else {
      chart.navigator.update({ enabled: true }, false);
    }

    const mainAxis = chart.xAxis?.[0];
    const scrollerAxis = chart.scroller?.xAxis;
    if (mainAxis && scrollerAxis) {
      const { dataMin, dataMax } = mainAxis.getExtremes();
      if (Number.isFinite(dataMin) && Number.isFinite(dataMax)) {
        scrollerAxis.setExtremes(dataMin, dataMax, false, false);
      }
    }

    queueReadySignal();
    chart.redraw();
  }, [chartRef, size, data, queueReadySignal]);
  const computedSeries = useMemo(() => {
    if (!Array.isArray(data)) return data;

    if (showMean) {
      const selectionSet =
        selectedSeriesNames === null
          ? new Set(baseSeries.map((series: any) => series.name))
          : new Set(selectedSeriesNames);

      const selectedSeries = baseSeries.filter((series: any) => selectionSet.has(series.name));

      if (!selectedSeries.length) {
        return data;
      }

      const pointStats = new Map<number, { sum: number; count: number; total: number }>();

      selectedSeries.forEach((series: any) => {
        (series.data || []).forEach((point: any) => {
          if (point === null || point === undefined) return;

          let xValue: number | undefined;
          let yValue: number | string | null | undefined;

          if (Array.isArray(point)) {
            xValue = point[0];
            yValue = point[1];
          } else if (typeof point === 'object') {
            xValue = point.x;
            yValue = (point as { y?: number | string | null }).y;
          }

          if (typeof xValue !== 'number') return;

          const entry = pointStats.get(xValue) ?? { sum: 0, count: 0, total: 0 };
          entry.total += 1;
          const numericValue =
            typeof yValue === 'number' ? yValue : typeof yValue === 'string' ? Number.parseFloat(yValue) : null;
          if (numericValue !== null && Number.isFinite(numericValue)) {
            entry.sum += numericValue;
            entry.count += 1;
          }
          pointStats.set(xValue, entry);
        });
      });

      const meanData: Array<[number, number | null]> = Array.from(pointStats.entries())
        .map(([x, stats]) => {
          const { sum, count } = stats;
          return [x, count > 0 ? sum / count : null] as [number, number | null];
        })
        .sort((a, b) => a[0] - b[0]);

      const hiddenBaseSeries = data.map((series: any) => {
        if (!series || !series.name) {
          return { ...series };
        }
        if (/^Navigator \d+$/.test(series.name)) {
          return { ...series };
        }
        const isSelected = selectionSet.has(series.name);
        return {
          ...series,
          data: [],
          visible: isSelected,
          showInLegend: true,
          showInNavigator: false,
          enableMouseTracking: false,
          states: {
            ...(series.states || {}),
            hover: { enabled: false },
          },
        };
      });

      return [
        ...hiddenBaseSeries,
        {
          type: 'line',
          name: 'Mean',
          data: meanData,
          color: '#7f7f7f',
          showInLegend: false,
          showInNavigator: true,
          lineWidth: 2.5,
          zIndex: 10,
          custom: {
            isMeanAggregate: true,
          },
        },
      ];
    }

    const selectionSet =
      selectedSeriesNames === null
        ? new Set(baseSeries.map((series: any) => series.name))
        : new Set(selectedSeriesNames);

    return data.map((series: any) => {
      if (!series || !series.name) {
        return { ...series };
      }
      if (/^Navigator \d+$/.test(series.name)) {
        return { ...series };
      }

      const isVisible = selectionSet.has(series.name);

      return {
        ...series,
        visible: isVisible,
        showInLegend: true,
        showInNavigator: series.showInNavigator !== false,
      };
    });
  }, [data, showMean, baseSeries, selectedSeriesNames]);

  const layoutSeriesCount = useMemo(() => {
    if (!Array.isArray(computedSeries)) {
      return baseSeriesCount;
    }

    return computedSeries.filter((series: any) => {
      if (!series || !series.name) return false;
      if (/^Navigator \d+$/.test(series.name)) return false;
      if (series?.custom?.isMeanAggregate) return false;
      return true;
    }).length;
  }, [computedSeries, baseSeriesCount]);

  const nonNegativeAxisFloor = useMemo(() => {
    if (typeof yAxisMin === 'number') {
      return undefined as number | undefined;
    }
    if (yAxisRangeMode === 'padded-visible') {
      return undefined as number | undefined;
    }
    if (Array.isArray(computedSeries)) {
      const { floor } = deriveNonNegativeAxisFloor(computedSeries as any);
      return floor;
    }
    return undefined as number | undefined;
  }, [computedSeries, yAxisMin, yAxisRangeMode]);

  const updateYAxisForVisibleWindow = useCallback(() => {
    const chart = chartRef?.current?.chart;
    if (!chart) return;

    const yAxis = chart.yAxis?.[0];
    const xAxis = chart.xAxis?.[0];
    if (!yAxis || !xAxis) return;

    if (yAxisRangeMode !== 'padded-visible') {
      yAxis.setExtremes(undefined, undefined, false, false);
      chart.redraw();
      return;
    }

    const xExtremes = xAxis.getExtremes();
    const minX = xExtremes?.min;
    const maxX = xExtremes?.max;
    const values = chart.series
      .filter(
        (series: any) =>
          series &&
          series.name &&
          !/^Navigator \d+$/.test(series.name) &&
          !(series?.options?.custom && series.options.custom.isMeanAggregate) &&
          series.visible !== false,
      )
      .flatMap((series: any) => {
        const renderedPoints = Array.isArray(series.points) ? (series.points as PointLike[]) : [];
        const sourcePoints =
          renderedPoints.length > 0
            ? renderedPoints
            : Array.isArray(series.options?.data)
              ? (series.options.data as PointLike[])
              : [];
        return sourcePoints
          .map((point: PointLike) => normalizePoint(point))
          .filter((point: { x: number; y: number } | null): point is { x: number; y: number } => point !== null)
          .filter(
            (point: { x: number; y: number }) =>
              !Number.isFinite(minX) || !Number.isFinite(maxX) || (point.x >= minX && point.x <= maxX),
          )
          .map((point: { x: number; y: number }) => point.y);
      })
      .filter((value: number) => Number.isFinite(value));

    const limits = deriveAxisLimits(values, yAxisPaddingRatio);
    if (typeof limits.min === 'number' && typeof limits.max === 'number') {
      yAxis.setExtremes(limits.min, limits.max, false, false);
    } else {
      yAxis.setExtremes(undefined, undefined, false, false);
    }
    chart.redraw();
  }, [computedSeries, yAxisPaddingRatio, yAxisRangeMode]);

  const restoreUserXZoomIfLost = useCallback(() => {
    if (!xRangeUserCustomRef.current) return;
    const chart = chartRef?.current?.chart;
    const xAxis = chart?.xAxis?.[0];
    const saved = savedUserXExtentsRef.current;
    if (!saved || !xAxis) return;
    requestAnimationFrame(() => {
      const cur = readFiniteXExtentsFromAxis(xAxis);
      if (cur && xExtentsDiffer(cur, saved)) {
        xAxis.setExtremes(saved.min, saved.max, false, false);
      }
    });
  }, []);

  const lastAppliedRangeRef = useRef<{ smaValue: unknown; selectedRangeSelector: unknown } | null>(null);
  useEffect(() => {
    const chart = chartRef?.current?.chart;
    if (!chart) return;

    const last = lastAppliedRangeRef.current;
    const rangeChanged = !last || last.smaValue !== smaValue || last.selectedRangeSelector !== selectedRangeSelector;

    if (rangeChanged) {
      setXRangeUserCustom(false);
      applyingRangePresetRef.current = true;
      const selectorChanged = !last || last.selectedRangeSelector !== selectedRangeSelector;
      const anchorFromButton = selectorChanged ? pendingDurationPresetAnchorRef.current : undefined;
      pendingDurationPresetAnchorRef.current = undefined;
      const anchorForApply = selectorChanged ? anchorFromButton : readMainAxisMaxForDurationPreset(chart);
      const applied = applyRangeSelectorToChart(chart, selectedRangeSelector, anchorForApply);
      if (applied) {
        lastAppliedRangeRef.current = { smaValue, selectedRangeSelector };
        savedUserXExtentsRef.current = null;
      }
      requestAnimationFrame(() => {
        applyingRangePresetRef.current = false;
      });
    } else {
      restoreUserXZoomIfLost();
    }

    chart.setTitle({ text: chartTitle ?? '' }, undefined, false);
    chart.redraw();
    queueReadySignal();
  }, [
    smaValue,
    selectedRangeSelector,
    chartRef,
    queueReadySignal,
    baseSeries,
    data,
    computedSeries,
    chartTitle,
    restoreUserXZoomIfLost,
  ]);

  useEffect(() => {
    const chart = chartRef?.current?.chart;
    if (!chart || xRangeUserCustom) return;

    const xValues = extractSeriesXValues(chart.series || []);
    if (!xValues.length) return;

    const xAxis = chart.xAxis?.[0];
    if (!xAxis || typeof xAxis.getExtremes !== 'function') return;

    const { min, max } = xAxis.getExtremes();
    if (!Number.isFinite(min) || !Number.isFinite(max)) return;
    if (areChartXExtremesCanonicalForRangeIndex(chart, selectedRangeSelector, min, max)) return;

    applyingRangePresetRef.current = true;
    const applied = applyRangeSelectorToChart(chart, selectedRangeSelector);
    if (applied) {
      savedUserXExtentsRef.current = null;
    }
    requestAnimationFrame(() => {
      applyingRangePresetRef.current = false;
    });
  }, [computedSeries, selectedRangeSelector, xRangeUserCustom]);

  useEffect(() => {
    if (!onExportMenuVisibilityChange) return;

    let cleanup: (() => void) | null = null;
    let disposed = false;

    const setup = () => {
      if (disposed) return;
      const chart = chartRef?.current?.chart;
      if (!chart) {
        requestAnimationFrame(setup);
        return;
      }

      const doc = chart.renderTo?.ownerDocument;
      if (!doc) return;

      const body = doc.body;
      if (!body) return;

      let lastVisible: boolean | null = null;
      const menuSelector = `.${exportMenuClassName}`;
      const computeVisibility = () => {
        const menu = body.querySelector(menuSelector) as HTMLElement | null;
        if (!menu) {
          return false;
        }
        const view = doc.defaultView;
        const style = view ? view.getComputedStyle(menu) : null;
        if (style) {
          if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
            return false;
          }
        } else if (menu.style.display === 'none') {
          return false;
        }

        return menu.childElementCount > 0;
      };

      const emitIfChanged = () => {
        const visible = computeVisibility();
        if (visible !== lastVisible) {
          lastVisible = visible;
          onExportMenuVisibilityChange(visible);
        }
      };

      emitIfChanged();

      const observer = new MutationObserver(() => {
        emitIfChanged();
      });

      observer.observe(body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style'],
      });

      const handleInteraction = () => {
        requestAnimationFrame(emitIfChanged);
      };

      body.addEventListener('click', handleInteraction, true);
      body.addEventListener('keydown', handleInteraction, true);

      cleanup = () => {
        observer.disconnect();
        body.removeEventListener('click', handleInteraction, true);
        body.removeEventListener('keydown', handleInteraction, true);
        onExportMenuVisibilityChange(false);
      };
    };

    setup();

    return () => {
      disposed = true;
      if (cleanup) {
        cleanup();
        cleanup = null;
      }
    };
  }, [onExportMenuVisibilityChange, exportMenuClassName]);

  useLayoutEffect(() => {
    restoreUserXZoomIfLost();
  }, [computedSeries, restoreUserXZoomIfLost]);

  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      updateYAxisForVisibleWindow();
    });
    return () => cancelAnimationFrame(rafId);
  }, [computedSeries, selectedRangeSelector, updateYAxisForVisibleWindow]);

  useEffect(() => {
    const chart = chartRef?.current?.chart;
    const xAxis = chart?.xAxis?.[0];
    if (!chart || !xAxis) return;

    const handleAfterSetExtremes = (e: any) => {
      const picked = pickXExtentsFromAfterSetExtremesEvent(e);
      if (picked && shouldPersistUserXExtentsFromEvent(e, { applyingRangePreset: applyingRangePresetRef.current })) {
        savedUserXExtentsRef.current = picked;
      }
      const chartInstance = chartRef?.current?.chart;
      const evMin = e?.min;
      const evMax = e?.max;
      if (
        !applyingRangePresetRef.current &&
        chartInstance &&
        Number.isFinite(evMin) &&
        Number.isFinite(evMax) &&
        !areChartXExtremesCanonicalForRangeIndex(chartInstance, selectedRangeSelector, evMin, evMax)
      ) {
        setXRangeUserCustom(true);
      }
      requestAnimationFrame(() => {
        updateYAxisForVisibleWindow();
      });
    };

    HighStock.addEvent(xAxis, 'afterSetExtremes', handleAfterSetExtremes as any);
    return () => {
      HighStock.removeEvent(xAxis, 'afterSetExtremes', handleAfterSetExtremes as any);
    };
  }, [computedSeries, selectedRangeSelector, updateYAxisForVisibleWindow]);

  const chartOptions = useMemo(() => {
    const legendY = layoutSeriesCount < 3 ? (chartSubtitle ? 40 : 26) : -14;
    const rangeSelectorY = () => {
      if (!chartSubtitle && isNarrowChart && dropdownLegendEnabled) {
        return 11;
      }
      if (!chartSubtitle && isNarrowChart && !dropdownLegendEnabled) {
        return -30;
      }
      if (!chartSubtitle) {
        return -27;
      }
      if (layoutSeriesCount === 1) {
        return -4;
      }
      return -6;
    };
    const titleWidth = () => {
      if (smaEnabled) {
        if (!isNarrowChart && meanEnabled) {
          return size.width - 320;
        }
        if (!isNarrowChart) {
          return size.width - 240;
        }
        return size.width - 240;
      }
      return size.width - 80;
    };

    const subTitleWidth = () => {
      if (!isNarrowChart && dropdownLegendEnabled) {
        return size.width - 340;
      }
      if (isNarrowChart && dropdownLegendEnabled) {
        return size.width - 320;
      }
      return size.width - 66;
    };

    return {
      custom: {
        component: 'YAxisLineChart',
        chartTitle,
        gridIndex,
        prism,
        resolvedGridHeight,
        chartHeight,
      },
      chart: {
        type: 'spline',
        marginLeft: 70,
        marginRight: !dropdownLegendEnabled && layoutSeriesCount < 3 ? 25 : undefined,
        marginTop: 120,
        spacingRight: 16,
        height: chartHeight,
        animation: false,
        style: {
          fontFamily: ['Montserrat', 'sans-serif'],
          left: '0px',
        },
        events: {
          click: (e: any) => {
            if (!onClick) return;
            const inputDate = e?.xAxis?.[0]?.value;
            if (!inputDate) return;
            const selectedDate = getFilterFormattedDate(inputDate);
            onClick({
              source: 'value',
              topic: selectedDate,
              symbol: selectedDate,
            });
          },
          load: function (this: HighStock.Chart) {
            syncLineChartLegendTooltips(this, tooltipCfg);
            bindLineChartTooltipVisibility(this, HighStock, onTooltipVisibilityChange);
          },
          redraw: function (this: HighStock.Chart) {
            syncLineChartLegendTooltips(this, tooltipCfg);
          },
          render: function (this: HighStock.Chart) {
            syncLineChartLegendTooltips(this, tooltipCfg);
          },
        },
      },
      xAxis: {
        type: 'datetime',
        ordinal: false,
        labels: {
          distance: plotBands ? 24 : 15,
        },
      },
      rangeSelector: {
        buttonSpacing: 12,
        inputEnabled: false,
        selected: xRangeUserCustom ? null : (selectedRangeSelector ?? DEFAULT_RANGE_SELECTOR_INDEX),
        floating: true,
        labelStyle: { display: 'none' },
        dropdown: 'never',
        x: 0,
        y: rangeSelectorY(),
        buttonTheme: {
          r: 15,
          fill: 'transparent',
          height: 26,
          style: {
            fill: 'transparent',
            fontSize: 13,
            marginRight: 15,
            color: '#33485E',
            background: 'transparent',
            fontWeight: 500,
          },
          states: {
            hover: {
              fill: 'transparent',
              style: {
                color: '#0081C4',
                background: 'transparent',
                fontWeight: 500,
              },
            },
            select: {
              fill: 'transparent',
              style: {
                color: '#0081C4',
                background: 'transparent',
                fontWeight: 600,
              },
            },
          },
        },
        buttonPosition: {
          x: -48,
          y: -78,
        },
        buttons: [
          {
            type: 'month',
            count: 1,
            text: '1M',
            title: 'View 1 month',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = readMainAxisMaxForDurationPreset(chartRef?.current?.chart);
                setXRangeUserCustom(false);
                setSelectedRangeSelector(0);
              },
            },
          },
          {
            type: 'month',
            count: 3,
            text: '3M',
            title: 'View 3 months',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = readMainAxisMaxForDurationPreset(chartRef?.current?.chart);
                setXRangeUserCustom(false);
                setSelectedRangeSelector(1);
              },
            },
          },
          {
            type: 'month',
            count: 6,
            text: '6M',
            title: 'View 6 months',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = readMainAxisMaxForDurationPreset(chartRef?.current?.chart);
                setXRangeUserCustom(false);
                setSelectedRangeSelector(2);
              },
            },
          },
          {
            type: 'ytd',
            text: 'YTD',
            title: 'View year to date',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = undefined;
                setXRangeUserCustom(false);
                setSelectedRangeSelector(3);
              },
            },
          },
          {
            type: 'year',
            count: 1,
            text: '1Y',
            title: 'View 1 year',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = readMainAxisMaxForDurationPreset(chartRef?.current?.chart);
                setXRangeUserCustom(false);
                setSelectedRangeSelector(4);
              },
            },
          },
          {
            type: 'all',
            text: 'Max',
            title: 'View all',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = undefined;
                setXRangeUserCustom(false);
                setSelectedRangeSelector(MAX_RANGE_SELECTOR_INDEX);
              },
            },
          },
        ],
      },
      exporting: {
        filename: exportFileName,
        csv: {
          columnHeaderFormatter: function (item: any) {
            if (item && item instanceof HighStock.Axis) {
              if ((item as unknown as any).type === 'datetime') {
                return 'Date';
              }
              return 'Category';
            }
            return {
              columnTitle: item.name,
            };
          },
          dateFormat: '%m/%d/%Y',
        },
        enabled: true,
        buttons: {
          contextButton: {
            x: 0,
            y: 2,
            symbol: 'ellipsisvertical',
            menuClassName: combinedMenuClassName,
            menuItems: getLineChartExportMenuItems(baseSeriesCount),
          },
        },
        fallbackToExportServer: false,
      },
      navigation: {
        x: -100,
        menuItemHoverStyle: {
          background: '#EAF2FF',
        },
        menuItemStyle: {
          height: 30,
          fill: '#33485E',
          fontSize: 15,
          padding: '6px 12px',
        },
      },
      title: {
        text: chartTitle,
        align: 'left',
        x: 12,
        y: 22,
        width: titleWidth(),
        style: {
          fontSize: '18px',
          color: 'var(--color-gray-6)',
          fontWeight: 500,
          letterSpacing: '0.5px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
      subtitle: {
        text: chartSubtitle,
        align: 'left',
        x: 12,
        y: 60,
        width: subTitleWidth(),
        style: {
          color: 'var(--color-gray-6)',
          fontWeight: 'normal',
          letterSpacing: '0.5px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: !dropdownLegendEnabled && layoutSeriesCount > 1,
        floating: layoutSeriesCount < 3,
        layout: layoutSeriesCount < 3 ? 'horizontal' : 'vertical',
        align: 'right',
        verticalAlign: 'top',
        x: layoutSeriesCount < 3 ? -4 : -10,
        y: legendY,
        maxHeight: 250,
        reversed: true,
        itemHiddenStyle: { color: '#666666', textDecoration: 'none' },
        events: {
          itemClick: legendEventsItemClick,
        },
      },
      yAxis: {
        width: 60,
        opposite: false,
        offset: 30,
        tickPixelInterval: 55,
        showFirstLabel: true,
        showLastLabel: true,
        labels: {
          style: {
            x: -40,
          },
          align: 'left',
          allowOverlap: true,
        },
        title: {
          text: chartYtitle,
          x: -17,
          style: {
            color: 'var(--color-gray-6)',
            fontWeight: 'normal',
            letterSpacing: '0.5px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
        plotLines: [
          {
            value: 0,
            width: 2,
            color: 'silver',
          },
        ],
        plotBands: plotBands,
        ...(nonNegativeAxisFloor !== undefined
          ? {
              floor: nonNegativeAxisFloor,
              softMin: nonNegativeAxisFloor,
              minPadding: 0,
            }
          : {}),
        ...(yAxisRangeMode === 'padded-visible'
          ? {
              startOnTick: false,
              endOnTick: false,
            }
          : {}),
        ...(yAxisRangeMode !== 'padded-visible' && typeof yAxisMin === 'number' ? { min: yAxisMin } : {}),
        ...(yAxisRangeMode !== 'padded-visible' && typeof yAxisMax === 'number' ? { max: yAxisMax } : {}),
      },
      plotOptions: {
        series: {
          selected: false,
          showInNavigator: true,
          animation: false,
          dataGrouping: { enabled: false },
          lineWidth: 2,
          line: {
            selected: false,
          },
          events: {
            afterAnimate: function (event: HighStock.SeriesAfterAnimateCallbackFunction) {
              const e = event as unknown as any;
              const chart = e.target.chart;
              const allSeries = chart?.series?.filter((s: any) => !/^Navigator \d+$/.test(s.name));
              if (!doneHide && allSeries?.length) {
                const hasExplicitSelection = Array.isArray(selectedSeriesNames);

                if (hasExplicitSelection) {
                  const selectionSet = new Set(selectedSeriesNames);
                  allSeries.forEach((s: any) => {
                    if (!s) return;
                    const shouldShow = selectionSet.size === 0 ? false : selectionSet.has(s.name);
                    s.setVisible(shouldShow, false);
                  });
                } else {
                  const totalSeries = allSeries.length;
                  const parsedLimit =
                    typeof numberOfLines === 'number' && Number.isFinite(numberOfLines) && numberOfLines > 0
                      ? Math.floor(numberOfLines)
                      : null;

                  const limitStartIndex =
                    parsedLimit !== null ? Math.max(totalSeries - Math.min(parsedLimit, totalSeries), 0) : 0;

                  allSeries.forEach((s: any, i: number) => {
                    if (!s) return;

                    let shouldShow = true;

                    if (parsedLimit !== null) {
                      shouldShow = i >= limitStartIndex;
                    } else if (totalSeries > 3) {
                      if (prism === 'Central Banks' && totalSeries > 8) {
                        shouldShow = i === 7 || i === 8;
                      } else {
                        shouldShow = i >= totalSeries - 1;
                      }
                    }

                    s.setVisible(shouldShow, false);
                  });
                }

                setDoneHide(true);
                updateSelectedSeriesFromChart(chart);
              }
            },
            click: (e: any) => {
              if (onClick) {
                const inputDate = e?.point?.category;
                if (inputDate) {
                  const selectedDate = getFilterFormattedDate(inputDate);
                  onClick({
                    source: 'value',
                    topic: selectedDate,
                    symbol: selectedDate,
                  });
                }
              }

              if (typeof onTopicExplainabilityRequest === 'function' && normalizedTopicForExplainability) {
                onTopicExplainabilityRequest(normalizedTopicForExplainability);
              }
            },
          },
        },
      },
      tooltip: {
        ...lineChartHighchartsTooltipCfg(tooltipCfg),
        useHTML: true,
        shape: 'rect',
        backgroundColor: '#fff',
        borderRadius: 5,
        borderColor: 'transparent',
        shadow: true,
      },
      navigator: {
        enabled: true,
        series: {
          fillOpacity: 0,
        },
      },
      lang: {
        noData: '',
      },
      noData: {
        style: {
          fontWeight: 'bold',
          fontSize: '15px',
          color: 'var(--color-chart-no-data)',
        },
      },
      series: computedSeries,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    computedSeries,
    layoutSeriesCount,
    data0,
    data1,
    tooltipCfg,
    chartTitle,
    chartSubtitle,
    chartYtitle,
    exportFileName,
    doneHide,
    chartHeight,
    gridIndex,
    prism,
    resolvedGridHeight,
    updateSelectedSeriesFromChart,
    legendEventsItemClick,
    baseSeriesNames,
    selectedSeriesNames,
    onChartReady,
    onTooltipVisibilityChange,
    combinedMenuClassName,
    normalizedTopicForExplainability,
    onTopicExplainabilityRequest,
    selectedRangeSelector,
    xRangeUserCustom,
    yAxisRangeMode,
    yAxisMin,
    yAxisMax,
  ]);

  useEffect(() => {
    if (!onMeanToggleStatusChange) return;
    onMeanToggleStatusChange({
      canShow: canShowMeanToggle,
      disabled: meanToggleDisabled,
    });
  }, [canShowMeanToggle, meanToggleDisabled, onMeanToggleStatusChange]);

  return (
    <Root $gridheight={resolvedGridHeight}>
      <HighchartContainer>
        <HighchartsReact
          containerProps={{ style: { height: '100%' } }}
          style={{ flex: 1 }}
          highcharts={HighStock}
          constructorType={'stockChart'}
          options={chartOptions ?? EMPTY_STOCK_OPTIONS}
          ref={chartRef}
        />
      </HighchartContainer>
    </Root>
  );
}
